import { useState } from 'react';

import {
  DialogRoute,
  DialogRouter,
  DialogRouterBody,
  DialogRouterHeader,
  DialogRouteTitle,
} from '@infinex/ui/components';
import { ConditionalMobileWebViewDialog } from '@infinex/web-shared/lib/mobile';

import { AddFundsScreen } from './gas-dialog-screens/AddFundsScreen';
import { GasAssetSelector } from './gas-dialog-screens/GasAssetSelector';
import { GasOnboarding } from './gas-dialog-screens/GasOnboarding';
import { GasTopUpConfirmation } from './gas-dialog-screens/GasTopUpConfirmation';
import { HomeScreen } from './gas-dialog-screens/HomeScreen';
import { MovingFunds } from './gas-dialog-screens/MovingFunds';
import { GasAssetSourceScreen } from './GasAssetSourceScreen';
import { GasDialogConfigProvider } from './GasDialogDependenciesContext';
import type { GasDialogConfig, GasInfo } from './types';

import { useOptimisticUiStateMutation } from '../../api/user/uiState';
import { useOptimisticUserUiState } from '../../api/user/userQueries';
import type { AssetSelection } from '../asset-select/type';

type GasDialogRoute =
  | 'home'
  | 'add-gas'
  | 'review'
  | 'topping-up'
  | 'select-asset'
  | 'select-asset-source';

export type GasDialogProps = GasInfo &
  GasDialogConfig & {
    /**
     * Override the dialog size.
     * @default 'small'
     */
    size?: 'small' | 'medium' | 'large' | 'fullscreen';
  };

export function GasDialog(props: GasDialogProps) {
  const {
    gasBalance,
    activities,
    isLoading: gasInfoLoading,
    networkCostsLegalUrl,
    requiresSessionKey,
    isPatron,
    onSeeAllActivity,
    onGoToWallet,
    size = 'small',
  } = props;

  const gasInfo: GasInfo = {
    gasBalance,
    activities,
    isLoading: gasInfoLoading,
  };
  const config: GasDialogConfig = {
    topUpType: 'prepaid_top_up',
    networkCostsLegalUrl,
    requiresSessionKey,
    isPatron,
    onSeeAllActivity,
    onGoToWallet,
  };

  const { data, status } = useOptimisticUserUiState();
  const { mutate: updateUiState } = useOptimisticUiStateMutation();
  const handleDismiss = () => {
    void updateUiState('gasFeeAccountOnboardingDismissedAt', Date.now());
  };

  const hasDismissedOnboarding =
    data?.gasFeeAccountOnboardingDismissedAt !== undefined;

  const [routeId, setRouteId] = useState<GasDialogRoute>('home');
  const [selectedAsset, setSelectedAsset] = useState<AssetSelection | null>(
    null
  );

  if (status === 'loading') return null;

  return (
    <GasDialogConfigProvider config={config}>
      <ConditionalMobileWebViewDialog
        mobileType="sheet"
        size={size}
        isDismissable
      >
        {hasDismissedOnboarding ? (
          <DialogRouter
            onRouteChange={(id) => setRouteId(id as GasDialogRoute)}
            allowPopNavigation={routeId !== 'topping-up'}
          >
            <DialogRouterHeader>
              <DialogRouteTitle id="home">Gas Account</DialogRouteTitle>

              <DialogRouteTitle id="add-gas">
                Top up Gas Account
              </DialogRouteTitle>

              <DialogRouteTitle id="review">Review</DialogRouteTitle>

              <DialogRouteTitle id="topping-up">Gas Account</DialogRouteTitle>

              <DialogRouteTitle id="select-asset">
                Select an asset
              </DialogRouteTitle>

              <DialogRouteTitle id="select-asset-source">
                Choose balance
              </DialogRouteTitle>
            </DialogRouterHeader>

            <DialogRouterBody>
              <DialogRoute id="home">
                <HomeScreen {...gasInfo} />
              </DialogRoute>

              <DialogRoute id="add-gas">
                <AddFundsScreen />
              </DialogRoute>

              <DialogRoute id="review">
                <GasTopUpConfirmation />
              </DialogRoute>

              <DialogRoute id="topping-up">
                <MovingFunds />
              </DialogRoute>

              <DialogRoute id="select-asset">
                {(state) => (
                  <GasAssetSelector
                    onSelectAssetSource={(asset) => {
                      setSelectedAsset(asset);
                      state.push('select-asset-source');
                    }}
                  />
                )}
              </DialogRoute>

              <DialogRoute id="select-asset-source">
                {(state) =>
                  selectedAsset ? (
                    <GasAssetSourceScreen
                      asset={selectedAsset}
                      onComplete={() => {
                        state.pop(); // pop to select-asset
                        state.pop(); // pop to add-gas
                      }}
                    />
                  ) : null
                }
              </DialogRoute>
            </DialogRouterBody>
          </DialogRouter>
        ) : (
          <GasOnboarding handleDismiss={handleDismiss} />
        )}
      </ConditionalMobileWebViewDialog>
    </GasDialogConfigProvider>
  );
}
